'use client'

import { useMemo } from 'react'
import { useLaunchDarkly } from '@/providers/LaunchDarkly'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'
import {
  DROP_SHIPMENT_ORDER_FLAG,
  isDropShipmentOrderFeatureFlagEnabled,
} from '@/utilities/orders/dropShipment/dropShipmentOrderAccess'
import {
  areClientFeatureFlagContextsEqual,
  type ClientFeatureFlagContext,
} from '@/utilities/client/clientPortalContext'

export { DROP_SHIPMENT_ORDER_FLAG, isDropShipmentOrderFeatureFlagEnabled }

export function useDropShipmentOrderAccess(selectedClientId?: string | null): {
  canAccessDropShipmentOrder: boolean
  isDropShipmentOrderAccessReady: boolean
  isDropShipmentOrderFeatureEnabled: boolean
} {
  const {
    flags,
    isLoading: areFeatureFlagsLoading,
    hasFetched: haveFeatureFlagsFetched,
    error: featureFlagsError,
    clientContext: loadedFeatureFlagContext,
  } = useLaunchDarkly()

  return useMemo(() => {
    const normalizedClientId = normalizeNullableClientId(selectedClientId)
    const expectedFeatureFlagContext: ClientFeatureFlagContext = normalizedClientId
      ? { kind: 'partner', partnerId: normalizedClientId }
      : { kind: 'none' }
    const isFeatureFlagPartnerAligned = areClientFeatureFlagContextsEqual(
      loadedFeatureFlagContext,
      expectedFeatureFlagContext,
    )
    const isDropShipmentOrderAccessReady =
      isFeatureFlagPartnerAligned &&
      !areFeatureFlagsLoading &&
      (haveFeatureFlagsFetched || featureFlagsError !== null)
    const isDropShipmentOrderFeatureEnabled = isDropShipmentOrderFeatureFlagEnabled(
      flags[DROP_SHIPMENT_ORDER_FLAG],
    )
    const canAccessDropShipmentOrder =
      isDropShipmentOrderAccessReady && isDropShipmentOrderFeatureEnabled

    return {
      canAccessDropShipmentOrder,
      isDropShipmentOrderAccessReady,
      isDropShipmentOrderFeatureEnabled,
    }
  }, [
    areFeatureFlagsLoading,
    featureFlagsError,
    flags,
    haveFeatureFlagsFetched,
    loadedFeatureFlagContext,
    selectedClientId,
  ])
}
